/**
 * Cross-app login redirect helpers.
 *
 * ZAFROI has a single canonical sign-in URL — `https://zafroi.com/login`.
 * Engage, ERP-on-subdomain, and any future tenant-facing app must bounce
 * unauthenticated visitors there rather than rendering their own local
 * login form. This keeps the user mental model simple ("I sign in at
 * zafroi.com") and avoids the surprise of seeing `engage.zafroi.com/login`
 * for the same workspace credentials.
 *
 * Override the canonical URL via `VITE_PLATFORM_LOGIN_URL` for local dev
 * (e.g. point both engage:5174 and erp:5173 at erp's local /login).
 */

const CANONICAL_LOGIN_URL =
  import.meta.env.VITE_PLATFORM_LOGIN_URL ?? 'https://zafroi.com/login';

/**
 * Build the canonical login URL with `?from=<current page>` so the apex
 * login can bounce back to the original deep-link after a successful
 * sign-in. Caller-supplied `from` overrides the inferred one.
 */
export function getPlatformLoginUrl(opts: { from?: string } = {}): string {
  const url = new URL(CANONICAL_LOGIN_URL);
  const from =
    opts.from ?? (typeof window !== 'undefined' ? window.location.href : '');
  if (from) url.searchParams.set('from', from);
  return url.toString();
}

/**
 * Are we already on the canonical login origin? Used by ERP (which IS
 * served at zafroi.com) to decide whether to render the local form or
 * redirect away. Engage is never canonical, so this always returns false
 * there — but the helper lives in the kit-shaped location for symmetry.
 */
export function isOnCanonicalLoginOrigin(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const canonical = new URL(CANONICAL_LOGIN_URL);
    return window.location.origin === canonical.origin;
  } catch {
    return false;
  }
}

/**
 * Hard-redirect to the canonical login. Uses `location.replace` so the
 * current entry is replaced — the browser back button won't return to
 * the (now-protected) page that triggered the bounce.
 */
export function redirectToPlatformLogin(): void {
  if (typeof window === 'undefined') return;
  window.location.replace(getPlatformLoginUrl());
}
